<template>
  <div class="config-page">
    <section class="config-toolbar">
      <div class="config-toolbar__copy">
        <div class="config-toolbar__title">活动配置</div>
        <div class="config-toolbar__desc">
          维护抽奖活动的信息、时间、文案与奖项。这里的配置会直接展示在小程序活动页，后续开新活动可以在这里新增一套。
        </div>
      </div>
      <div class="config-toolbar__actions">
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
        <button class="primary-button" type="button" @click="startCreateCampaign">新建活动</button>
      </div>
    </section>

    <section class="config-workbench">
      <aside class="campaign-list" aria-label="活动列表">
        <div v-if="loading" class="list-state">正在加载活动…</div>
        <div v-else-if="!campaigns.length" class="list-state">还没有活动，点右上角新建。</div>
        <div v-else class="list-body">
          <div
            v-for="item in campaigns"
            :key="String(item.id)"
            class="campaign-item"
            :class="{ 'campaign-item--active': form.id === item.id }"
            @click="selectCampaign(item.id)"
          >
            <div class="campaign-item__top">
              <span class="campaign-item__name">{{ item.name }}</span>
              <span class="status-pill" :class="'status-pill--' + String(item.status || '').toLowerCase()">
                {{ statusLabel(item.status) }}
              </span>
            </div>
            <div class="campaign-item__meta">{{ item.code }}</div>
            <div class="campaign-item__meta">
              报名 {{ shortDate(item.entryOpenAt) }} ~ {{ shortDate(item.entryCloseAt) }}｜开奖 {{ shortDate(item.drawAt) }}
            </div>
            <div class="campaign-item__meta">
              报名 {{ item.entryCount || 0 }} 人 · 中奖 {{ item.winnerCount || 0 }} 人
            </div>
          </div>
        </div>
      </aside>

      <article class="config-editor">
        <header class="editor-header">
          <div>
            <div class="editor-header__eyebrow">{{ form.id ? "正在编辑活动" : "新建活动" }}</div>
            <h2>{{ form.name || "活动配置" }}</h2>
          </div>
          <span v-if="form.id" class="editor-header__hint">更新于 {{ formatDateTime(activeCampaign && activeCampaign.updatedAt) }}</span>
        </header>

        <div class="editor-body">
          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>基本信息</h3>
                <p>活动名称与副标题会显示在小程序活动页顶部。</p>
              </div>
            </div>
            <div class="field-grid">
              <label class="field">
                <span class="field__label">活动名称</span>
                <input v-model.trim="form.name" type="text" maxlength="128" placeholder="例如：爬仔日记 × 纯白社群 专属爬友福利活动" />
              </label>
              <label class="field">
                <span class="field__label">活动标识</span>
                <input v-model.trim="form.code" type="text" maxlength="64" :disabled="Boolean(form.id)" placeholder="例如：pachong-2026-autumn" />
                <span class="field__hint">{{ form.id ? "标识已锁定：报名与开奖记录都挂在它上面。" : "小写字母、数字和连字符，以字母开头。" }}</span>
              </label>
            </div>
            <label class="field">
              <span class="field__label">副标题</span>
              <input v-model.trim="form.subtitle" type="text" maxlength="255" placeholder="例如：随手分享你的养宠日常，高端爬缸抱回家" />
            </label>
            <label class="field">
              <span class="field__label">活动状态</span>
              <select v-model="form.status">
                <option value="DRAFT">草稿（小程序不展示）</option>
                <option value="READY">待开始（按报名时间自动开放）</option>
                <option value="RUNNING">进行中</option>
                <option value="CLOSED">已封榜（停止报名）</option>
                <option value="SETTLED">已开奖（活动结束）</option>
              </select>
            </label>
          </section>

          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>活动时间</h3>
                <p>报名开放时间到点自动生效；封榜时间用于热度统计截止。</p>
              </div>
            </div>
            <div class="field-grid">
              <label class="field">
                <span class="field__label">报名开始</span>
                <input v-model="form.entryOpenAt" type="datetime-local" />
              </label>
              <label class="field">
                <span class="field__label">报名截止</span>
                <input v-model="form.entryCloseAt" type="datetime-local" />
              </label>
              <label class="field">
                <span class="field__label">热度封榜</span>
                <input v-model="form.heatFreezeAt" type="datetime-local" />
              </label>
              <label class="field">
                <span class="field__label">开奖时间</span>
                <input v-model="form.drawAt" type="datetime-local" />
              </label>
            </div>
            <label class="field">
              <span class="field__label">报名人数上限 <small>选填</small></span>
              <input v-model.number="form.entryLimit" type="number" min="0" placeholder="留空表示不限制" />
            </label>
          </section>

          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>活动文案</h3>
                <p>这些文案会显示在小程序活动页，留空时页面用默认写法。</p>
              </div>
            </div>
            <label class="field">
              <span class="field__label">活动规则</span>
              <textarea v-model="form.rulesContent" rows="5" placeholder="一条一行，例如：&#10;1. 终极大奖：活动期间帖子热度第 1 名&#10;2. 一二三等奖：合规参与用户中随机抽取" />
            </label>
            <label class="field">
              <span class="field__label">参与流程</span>
              <textarea v-model="form.stepsContent" rows="3" placeholder="一行一条，例如：&#10;1 在广场发布一条真实养宠日常，发布时勾选参与活动&#10;2 回到活动页提交报名并填写收货信息" />
              <span class="field__hint">显示在活动页「参与流程」区块，顺序即列表顺序。</span>
            </label>
            <label class="field">
              <span class="field__label">活动话题</span>
              <select v-model="form.topicCode">
                <option value="">不绑定话题</option>
                <option v-for="topic in topics" :key="topic.topicCode" :value="topic.topicCode">
                  #{{ topic.topicName }}（{{ topic.topicCode }}）
                </option>
              </select>
              <span class="field__hint">
                绑定后，用户在广场发帖时勾选「参与活动」，帖子会自动带上这个话题并作为参赛帖。
              </span>
            </label>
            <label class="field">
              <span class="field__label">主按钮文案</span>
              <input v-model.trim="form.entryButtonText" type="text" maxlength="32" placeholder="例如：参与活动" />
            </label>
            <label class="field">
              <span class="field__label">开奖前公示文案</span>
              <input v-model.trim="form.drawPendingText" type="text" maxlength="500" placeholder="例如：开奖后中奖名单会在这里公示" />
            </label>
            <label class="field">
              <span class="field__label">领奖说明</span>
              <textarea v-model="form.claimNotice" rows="2" placeholder="开奖后展示给中奖用户的说明，例如领奖方式与时限" />
            </label>
            <label class="field">
              <span class="field__label">报名提示 <small>选填</small></span>
              <input v-model.trim="form.noticeContent" type="text" maxlength="500" placeholder="例如：发布一条真实养宠日常后，回到这里提交报名" />
            </label>
          </section>

          <section class="editor-section">
            <div class="section-heading">
              <div>
                <h3>奖项设置</h3>
                <p>「热度排名」按活动期内参赛帖的点赞与评论热度取第 1 名；「随机抽取」在审核通过的用户中抽。</p>
              </div>
              <button v-if="form.id" class="ghost-button" type="button" @click="startCreatePrize">新增奖项</button>
            </div>
            <p v-if="!form.id" class="section-empty">先保存活动，再配置奖项。</p>
            <div v-else-if="!(form.prizes || []).length" class="section-empty">还没有奖项，点右上角新增。</div>
            <div v-else class="prize-list">
              <div v-for="(prize, index) in form.prizes" :key="String(prize.id)" class="prize-row">
                <span class="prize-level">{{ prize.levelName || levelLabel(prize.levelCode) }}</span>
                <span class="prize-name">{{ prize.name }}</span>
                <span class="prize-mode">{{ prize.awardMode === "HOT_RANK" ? "热度排名" : "随机抽取" }}</span>
                <span class="prize-quantity">{{ prize.quantity || 1 }} 名</span>
                <div class="prize-actions">
                  <button type="button" class="order-button" :disabled="index === 0" @click="movePrize(prize, 'UP')">↑</button>
                  <button type="button" class="order-button" :disabled="index === form.prizes.length - 1" @click="movePrize(prize, 'DOWN')">↓</button>
                  <button type="button" class="link-button" @click="startEditPrize(prize)">编辑</button>
                  <button type="button" class="link-button link-button--danger" @click="removePrize(prize)">删除</button>
                </div>
              </div>
            </div>
          </section>
        </div>

        <footer class="editor-footer">
          <div class="editor-footer__state">
            <span v-if="message" :class="{ 'text-error': messageError }">{{ message }}</span>
            <span v-else>保存后小程序下次进入活动页即可看到。</span>
          </div>
          <div class="editor-footer__actions">
            <button v-if="form.id" class="danger-button" type="button" :disabled="saving" @click="removeCampaign">删除活动</button>
            <button v-if="form.id" class="ghost-button" type="button" :disabled="saving" @click="startCreateCampaign">改为新建</button>
            <button class="primary-button" type="button" :disabled="saving" @click="submitCampaign">
              {{ saving ? "保存中…" : "保存活动" }}
            </button>
          </div>
        </footer>
      </article>
    </section>

    <div v-if="prizeModal" class="modal-backdrop" @click.self="prizeModal = false">
      <section class="modal-card" role="dialog" aria-modal="true">
        <header class="modal-head">
          <h3>{{ prizeForm.id ? "编辑奖项" : "新增奖项" }}</h3>
          <button type="button" @click="prizeModal = false">×</button>
        </header>
        <div class="modal-body">
          <div class="field-grid">
            <label class="field">
              <span class="field__label">奖项级别</span>
              <select v-model="prizeForm.levelCode">
                <option value="GRAND">终极大奖</option>
                <option value="FIRST">一等奖</option>
                <option value="SECOND">二等奖</option>
                <option value="THIRD">三等奖</option>
              </select>
            </label>
            <label class="field">
              <span class="field__label">级别显示名</span>
              <input v-model.trim="prizeForm.levelName" type="text" maxlength="32" placeholder="例如：特等奖、人气奖" />
            </label>
          </div>
          <label class="field">
            <span class="field__label">奖品名称</span>
            <input v-model.trim="prizeForm.name" type="text" maxlength="128" placeholder="例如：达芬奇 pro 爬缸" />
          </label>
          <label class="field">
            <span class="field__label">奖品说明 <small>选填</small></span>
            <input v-model.trim="prizeForm.description" type="text" maxlength="255" placeholder="例如：活动期间帖子热度第 1 名" />
          </label>
          <label class="field">
            <span class="field__label">奖品图片 <small>选填</small></span>
            <input v-model.trim="prizeForm.imageUrl" type="text" maxlength="255" placeholder="图片地址，留空则不展示图片" />
          </label>
          <div class="field-grid">
            <label class="field">
              <span class="field__label">评奖方式</span>
              <select v-model="prizeForm.awardMode">
                <option value="RANDOM">随机抽取</option>
                <option value="HOT_RANK">热度排名第 1</option>
              </select>
            </label>
            <label class="field">
              <span class="field__label">中奖名额</span>
              <input v-model.number="prizeForm.quantity" type="number" min="1" />
            </label>
          </div>
        </div>
        <footer class="modal-foot">
          <button class="plain-button" type="button" @click="prizeModal = false">取消</button>
          <button class="primary-button" type="button" :disabled="savingPrize" @click="submitPrize">
            {{ savingPrize ? "保存中…" : "保存奖项" }}
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import {
  deleteActivityConfig,
  deleteActivityPrize,
  fetchActivityConfig,
  fetchActivityConfigs,
  moveActivityPrize,
  saveActivityConfig,
  saveActivityPrize
} from "../api/activityConfig"
import { fetchSquareTopics } from "../api/squareTopic"

function emptyCampaign() {
  return {
    id: null,
    code: "",
    name: "",
    subtitle: "",
    coverUrl: "",
    status: "READY",
    entryOpenAt: "",
    entryCloseAt: "",
    heatFreezeAt: "",
    drawAt: "",
    rulesContent: "",
    stepsContent: "",
    claimNotice: "",
    topicCode: "",
    drawPendingText: "",
    entryButtonText: "",
    noticeContent: "",
    entryLimit: null,
    prizes: []
  }
}

function emptyPrize() {
  return {
    id: null,
    levelCode: "FIRST",
    levelName: "",
    name: "",
    description: "",
    imageUrl: "",
    awardMode: "RANDOM",
    quantity: 1
  }
}

// 后台输入用 datetime-local（yyyy-MM-ddTHH:mm），接口用同样的 ISO 格式
function toInputValue(value) {
  if (!value) return ""
  return String(value).replace(" ", "T").slice(0, 16)
}

function toApiValue(value) {
  if (!value) return null
  // datetime-local 给的是 2026-11-20T23:59，接口按 ISO 解析，补上秒即可
  const normalized = String(value).trim()
  if (normalized.length === 16 && normalized.includes("T")) {
    return normalized + ":00"
  }
  return normalized
}

export default {
  name: "ActivityConfigView",
  data() {
    return {
      loading: false,
      saving: false,
      savingPrize: false,
      message: "",
      messageError: false,
      campaigns: [],
      topics: [],
      form: emptyCampaign(),
      prizeModal: false,
      prizeForm: emptyPrize()
    }
  },
  computed: {
    activeCampaign() {
      if (!this.form.id) return null
      return this.campaigns.find(item => item.id === this.form.id) || null
    }
  },
  created() {
    this.loadTopics()
    this.reload()
  },
  methods: {
    async loadTopics() {
      try {
        const res = await fetchSquareTopics()
        this.topics = Array.isArray(res.data) ? res.data.filter(item => item.status === 1) : []
      } catch (error) {
        this.topics = []
      }
    },
    async reload() {
      this.loading = true
      try {
        const res = await fetchActivityConfigs()
        this.campaigns = Array.isArray(res.data) ? res.data : []
        if (this.form.id) {
          const exists = this.campaigns.some(item => item.id === this.form.id)
          if (exists) await this.selectCampaign(this.form.id, true)
          else this.form = emptyCampaign()
        }
      } catch (error) {
        this.campaigns = []
        this.flash((error && error.message) || "活动加载失败", true)
      } finally {
        this.loading = false
      }
    },
    async selectCampaign(id, silent = false) {
      try {
        const res = await fetchActivityConfig(id)
        const data = res.data || {}
        this.form = {
          ...emptyCampaign(),
          ...data,
          entryOpenAt: toInputValue(data.entryOpenAt),
          entryCloseAt: toInputValue(data.entryCloseAt),
          heatFreezeAt: toInputValue(data.heatFreezeAt),
          drawAt: toInputValue(data.drawAt),
          coverUrl: data.coverUrl || "",
          rulesContent: data.rulesContent || "",
          stepsContent: data.stepsContent || "",
          claimNotice: data.claimNotice || "",
          topicCode: data.topicCode || "",
          drawPendingText: data.drawPendingText || "",
          entryButtonText: data.entryButtonText || "",
          noticeContent: data.noticeContent || "",
          prizes: Array.isArray(data.prizes) ? data.prizes : []
        }
        if (!silent) {
          this.message = ""
          this.messageError = false
        }
      } catch (error) {
        this.flash((error && error.message) || "活动读取失败", true)
      }
    },
    startCreateCampaign() {
      this.form = emptyCampaign()
      this.message = ""
      this.messageError = false
    },
    async submitCampaign() {
      if (!this.form.name) {
        this.flash("请填写活动名称", true)
        return
      }
      if (!this.form.id && !this.form.code) {
        this.flash("请填写活动标识", true)
        return
      }
      this.saving = true
      try {
        const res = await saveActivityConfig({
          id: this.form.id,
          code: this.form.id ? undefined : this.form.code,
          name: this.form.name,
          subtitle: this.form.subtitle || null,
          coverUrl: this.form.coverUrl || null,
          status: this.form.status,
          entryOpenAt: toApiValue(this.form.entryOpenAt),
          entryCloseAt: toApiValue(this.form.entryCloseAt),
          heatFreezeAt: toApiValue(this.form.heatFreezeAt),
          drawAt: toApiValue(this.form.drawAt),
          rulesContent: this.form.rulesContent || null,
          stepsContent: this.form.stepsContent || null,
          claimNotice: this.form.claimNotice || null,
          topicCode: this.form.topicCode || null,
          drawPendingText: this.form.drawPendingText || null,
          entryButtonText: this.form.entryButtonText || null,
          noticeContent: this.form.noticeContent || null,
          entryLimit: this.form.entryLimit ? Number(this.form.entryLimit) : null
        })
        this.flash(res.message || "已保存")
        const savedId = res.data && res.data.id
        await this.reload()
        if (savedId) await this.selectCampaign(savedId, true)
      } catch (error) {
        this.flash((error && error.message) || "保存失败", true)
      } finally {
        this.saving = false
      }
    },
    async removeCampaign() {
      if (!this.form.id) return
      if (!window.confirm(`确认删除「${this.form.name || "该活动"}」？删除后小程序不再展示这个活动。`)) return
      this.saving = true
      try {
        await deleteActivityConfig(this.form.id)
        this.flash("活动已删除")
        this.startCreateCampaign()
        await this.reload()
      } catch (error) {
        this.flash((error && error.message) || "删除失败", true)
      } finally {
        this.saving = false
      }
    },
    startCreatePrize() {
      this.prizeForm = emptyPrize()
      this.prizeModal = true
    },
    startEditPrize(prize) {
      this.prizeForm = {
        id: prize.id,
        levelCode: prize.levelCode || "FIRST",
        levelName: prize.levelName || "",
        name: prize.name || "",
        description: prize.description || "",
        imageUrl: prize.imageUrl || "",
        awardMode: prize.awardMode || "RANDOM",
        quantity: prize.quantity || 1
      }
      this.prizeModal = true
    },
    async submitPrize() {
      if (!this.prizeForm.name) {
        this.flash("请填写奖品名称", true)
        return
      }
      this.savingPrize = true
      try {
        await saveActivityPrize({
          id: this.prizeForm.id,
          campaignId: this.form.id,
          levelCode: this.prizeForm.levelCode,
          levelName: this.prizeForm.levelName || null,
          name: this.prizeForm.name,
          description: this.prizeForm.description || null,
          imageUrl: this.prizeForm.imageUrl || null,
          awardMode: this.prizeForm.awardMode,
          quantity: this.prizeForm.quantity ? Number(this.prizeForm.quantity) : 1
        })
        this.prizeModal = false
        this.flash("奖项已保存")
        await this.selectCampaign(this.form.id, true)
      } catch (error) {
        this.flash((error && error.message) || "奖项保存失败", true)
      } finally {
        this.savingPrize = false
      }
    },
    async removePrize(prize) {
      if (!window.confirm(`确认删除奖项「${prize.name}」？`)) return
      try {
        await deleteActivityPrize(prize.id)
        this.flash("奖项已删除")
        await this.selectCampaign(this.form.id, true)
      } catch (error) {
        this.flash((error && error.message) || "奖项删除失败", true)
      }
    },
    async movePrize(prize, direction) {
      try {
        const res = await moveActivityPrize(prize.id, direction)
        if (Array.isArray(res.data)) {
          this.form.prizes = res.data
        } else {
          await this.selectCampaign(this.form.id, true)
        }
      } catch (error) {
        this.flash((error && error.message) || "调整顺序失败", true)
      }
    },
    flash(text, isError = false) {
      this.message = text
      this.messageError = isError
    },
    statusLabel(status) {
      return {
        DRAFT: "草稿",
        READY: "待开始",
        RUNNING: "进行中",
        CLOSED: "已封榜",
        SETTLED: "已开奖"
      }[status] || "待开始"
    },
    levelLabel(levelCode) {
      return {
        GRAND: "终极大奖",
        FIRST: "一等奖",
        SECOND: "二等奖",
        THIRD: "三等奖"
      }[levelCode] || "奖品"
    },
    shortDate(value) {
      if (!value) return "—"
      return String(value).replace("T", " ").slice(5, 16)
    },
    formatDateTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    }
  }
}
</script>

<style scoped>
.config-page { display: flex; flex-direction: column; gap: 18px; height: 100%; color: #17221d; }
.config-toolbar { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; padding: 20px 22px; background: #fff; border: 1px solid #e5ebe7; border-radius: 16px; box-shadow: 0 10px 26px rgba(23, 34, 29, .05); flex-wrap: wrap; }
.config-toolbar__title { font-size: 20px; font-weight: 700; }
.config-toolbar__desc { margin-top: 6px; max-width: 660px; color: #6c7a73; font-size: 13px; line-height: 1.7; }
.config-toolbar__actions { display: flex; gap: 10px; }
.ghost-button, .primary-button, .danger-button { height: 36px; padding: 0 16px; border-radius: 10px; font-size: 13px; cursor: pointer; }
.ghost-button { border: 1px solid #dfe6e1; background: #fff; color: #33463b; }
.primary-button { border: 0; background: #0fbd68; color: #fff; }
.danger-button { border: 1px solid #f0cdc5; background: #fff; color: #b23f2c; }
.plain-button { height: 36px; padding: 0 16px; border: 0; border-radius: 10px; background: #f2f5f3; color: #33463b; font-size: 13px; cursor: pointer; }
.ghost-button:disabled, .primary-button:disabled, .danger-button:disabled, .order-button:disabled { opacity: .55; cursor: not-allowed; }
.link-button { border: 0; background: transparent; color: #0f7a4a; font-size: 12.5px; cursor: pointer; }
.link-button--danger { color: #b23f2c; }
.config-workbench { display: grid; grid-template-columns: 330px minmax(0, 1fr); gap: 18px; flex: 1; min-height: 0; }
.campaign-list, .config-editor { display: flex; flex-direction: column; min-height: 0; background: #fff; border: 1px solid #e5ebe7; border-radius: 16px; box-shadow: 0 10px 26px rgba(23, 34, 29, .05); }
.list-body { flex: 1; overflow-y: auto; padding: 8px; }
.list-state { padding: 44px 20px; color: #86928c; font-size: 13px; text-align: center; }
.campaign-item { padding: 12px; border-radius: 12px; cursor: pointer; }
.campaign-item:hover { background: #f7faf8; }
.campaign-item--active { background: #eefaf2; }
.campaign-item__top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.campaign-item__name { font-size: 13.5px; font-weight: 600; line-height: 1.5; }
.campaign-item__meta { margin-top: 5px; color: #86928c; font-size: 11.5px; line-height: 1.6; }
.status-pill { flex: 0 0 auto; padding: 3px 8px; border-radius: 999px; background: #f2f5f3; color: #4d5c54; font-size: 11px; }
.status-pill--running { background: #e8f8ef; color: #14794a; }
.status-pill--settled { background: #eef2ff; color: #3b4f9e; }
.status-pill--draft { background: #f5f6f5; color: #86928c; }
.editor-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 18px 22px; border-bottom: 1px solid #eef2ef; flex-wrap: wrap; }
.editor-header__eyebrow { color: #86928c; font-size: 12px; }
.editor-header h2 { margin: 6px 0 0; font-size: 19px; }
.editor-header__hint { color: #86928c; font-size: 12px; }
.editor-body { flex: 1; overflow-y: auto; padding: 20px 22px; display: flex; flex-direction: column; gap: 20px; }
.editor-section { padding: 16px 18px; border: 1px solid #eef2ef; border-radius: 14px; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.section-heading h3 { margin: 0; font-size: 15px; }
.section-heading p { margin: 4px 0 0; color: #86928c; font-size: 12px; line-height: 1.7; }
.section-empty { margin: 0; padding: 14px 16px; border-radius: 12px; background: #f7faf8; color: #6c7a73; font-size: 12.5px; }
.field-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 0 16px; }
.field { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
.field__label { font-size: 13px; color: #33463b; }
.field__label small { color: #97a29c; font-size: 12px; }
.field input[type="text"], .field input[type="number"], .field input[type="datetime-local"], .field select, .field textarea { padding: 0 12px; border: 1px solid #dfe6e1; border-radius: 10px; background: #fff; color: #1f2d26; font-size: 13px; outline: none; font-family: inherit; }
.field input[type="text"], .field input[type="number"], .field input[type="datetime-local"], .field select { height: 38px; }
.field textarea { padding: 10px 12px; line-height: 1.7; resize: vertical; }
.field input:disabled { background: #f6f8f6; color: #86928c; }
.field__hint { color: #97a29c; font-size: 12px; line-height: 1.7; }
.prize-list { border-top: 1px solid #eef2ef; }
.prize-row { display: flex; align-items: center; gap: 12px; padding: 11px 0; border-bottom: 1px solid #f4f7f5; }
.prize-level { flex: 0 0 64px; color: #6c7a73; font-size: 12.5px; }
.prize-name { flex: 1 1 auto; min-width: 0; font-size: 13.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.prize-mode { flex: 0 0 auto; color: #6c7a73; font-size: 12px; }
.prize-quantity { flex: 0 0 auto; color: #6c7a73; font-size: 12px; }
.prize-actions { display: flex; align-items: center; gap: 8px; }
.order-button { width: 24px; height: 24px; border: 1px solid #dfe6e1; border-radius: 6px; background: #fff; color: #4d5c54; font-size: 12px; line-height: 1; cursor: pointer; }
.editor-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px 22px; border-top: 1px solid #eef2ef; flex-wrap: wrap; }
.editor-footer__state { color: #6c7a73; font-size: 13px; }
.text-error { color: #b23f2c; }
.editor-footer__actions { display: flex; gap: 10px; }
.modal-backdrop { position: fixed; inset: 0; z-index: 90; display: grid; place-items: center; padding: 24px; background: rgba(22, 29, 36, .5); }
.modal-card { width: min(560px, calc(100vw - 48px)); max-height: calc(100vh - 48px); display: flex; flex-direction: column; overflow: hidden; border-radius: 12px; background: #fff; box-shadow: 0 24px 70px rgba(17, 25, 31, .24); }
.modal-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #eef2ef; }
.modal-head h3 { margin: 0; font-size: 16px; }
.modal-head button { width: 32px; height: 32px; border: 0; border-radius: 6px; background: transparent; color: #68747f; font-size: 22px; cursor: pointer; }
.modal-body { flex: 1; overflow-y: auto; padding: 8px 20px 20px; }
.modal-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid #eef2ef; }
@media (max-width: 1080px) { .config-workbench { grid-template-columns: 1fr; } }
</style>
